import { createContext, type ReactNode, useContext } from 'react';
import { type StoreApi, useStore as useZustandStore } from 'zustand';
import type { CameraSlice } from '../camera/types';
import type { CanvasHandler } from '../feature-system/event-hub/types';

type CanvasContextValue<Root> = {
  store: StoreApi<Root>;
  handlers: CanvasHandler<Root>[];
};

// The store type is erased to `unknown` here because the concrete Root varies
// per canvas instance. Consumers re-assert via createStoreHooks<Root>().
// Type mismatches are not caught at compile time — this is an accepted tradeoff
// for composability.
const CanvasContext = createContext<CanvasContextValue<unknown> | null>(null);

type CanvasProviderProps<Root> = {
  children: ReactNode;
  store: StoreApi<Root>;
  handlers: CanvasHandler<Root>[];
};

export const CanvasProvider = <Root,>({
  children,
  store,
  handlers,
}: CanvasProviderProps<Root>) => (
  <CanvasContext.Provider
    value={{ store, handlers } as CanvasContextValue<unknown>}
  >
    {children}
  </CanvasContext.Provider>
);

function useCanvasContext<Root>(): CanvasContextValue<Root> {
  const context = useContext(CanvasContext);
  if (!context) {
    throw new Error('useCanvasContext must be used within CanvasProvider');
  }
  return context as CanvasContextValue<Root>;
}

export function useStoreApi<Root = unknown>(): StoreApi<Root> {
  return useCanvasContext<Root>().store;
}

export function useHandlers<Root = unknown>(): CanvasHandler<Root>[] {
  return useCanvasContext<Root>().handlers;
}

export function useStoreSelector<Root, T>(selector: (state: Root) => T): T {
  const store = useStoreApi<Root>();
  return useZustandStore(store, selector);
}

export function createStoreHooks<Root = CameraSlice>() {
  return {
    useStore: <T,>(selector: (state: Root) => T): T =>
      useStoreSelector<Root, T>(selector),
    useStoreApi: (): StoreApi<Root> => useStoreApi<Root>(),
    useHandlers: (): CanvasHandler<Root>[] => useHandlers<Root>(),
  };
}
